import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, forkJoin } from 'rxjs';
import { AppConfigService } from '@core/app-config/app-config.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { AvailableTranslationsService } from '@core/translations/available-translations.service';
import { TranslateService } from '@ngx-translate/core';
import { InitialConfigService } from '@core/app-config/initial-config.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { LoggerService } from '@core/logger/logger.service';
import { IInitialConfig } from '@core/venue-config/initial-config.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { WhenAttendingService } from '@app/when-attending/when-attending.service';
import { DateFormatService } from '@core/date/date-format.service';
import { IVenueConfig, IVenueConfigWithProducts } from '@core/venue-config/models/venue-config.model';
import { AuthService } from '@core/auth/auth.service';
import { AccountService } from '@core/account/account.service';
import { IBasicCustomer } from '@core/customer/basic-customer.model';
import { HttpErrorResponse } from '@angular/common/http';
import { OrderService } from '@core/order/order.service';
import { StorageService } from '@core/storage/storage.service';
import { CPickUpTableNumber } from '@dialog/dialog-table-number/dialog-table-number.component.model';
import { MatomoAnalyticsService } from '../analytics/matomo-analytics.service';
import { GuestCountService } from '@core/guest-count/guest-count.service';
import { ProductListService } from '@core/product/product-list.service';
import { AutomaticDiscountService } from '@core/automatic-discount/automatic-discount.service';
import { IAutomaticDiscount } from '../automatic-discount/automatic-discount.model';
import { ProductBestPricingService } from '@core/product/product-best-price.service';

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {
  private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isAppInitialized = false;
  private pendingAutomaticDiscount: IAutomaticDiscount | undefined;

  constructor(
    private logger: LoggerService,
    private appConfigService: AppConfigService,
    private availableTranslationsService: AvailableTranslationsService,
    private translateService: TranslateService,
    private initialConfigService: InitialConfigService,
    private navigationService: NavigationService,
    private venueConfigService: VenueConfigService,
    private whenAttendingService: WhenAttendingService,
    private dateFormatService: DateFormatService,
    private authService: AuthService,
    private accountService: AccountService,
    private orderService: OrderService,
    private storageService: StorageService,
    private matomoAnalyticsService: MatomoAnalyticsService,
    private guestCountService: GuestCountService,
    private productListService: ProductListService,
    private automaticDiscountService: AutomaticDiscountService,
    private productBestPricingService: ProductBestPricingService
  ) {}

  getIsAppInitialized(): boolean {
    return this.isAppInitialized;
  }

  getIsLoading$(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  setIsLoading(value: boolean): void {
    this.isLoading$.next(value);
  }

  /**
   * Build initial config
   * Load products, settings, translations in parallel
   * Set auth state and load account
   */
  bootstrapApplication(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (this.isAppInitialized) {
      this.logger.info('BootstrapService [bootstrapApplication]: Skipping bootstrap - app already initialized');
      return of(true);
    }

    const timeStart = Date.now();
    this.setIsLoading(true);
    const discountCode = route.queryParams.code;

    const baseUrl = this.appConfigService.getEnvValue<string>('rollerApiBaseUrl');

    return this.initialConfigService.loadInitialConfig(baseUrl, route).pipe(
      tap((config) => {
        this.initialConfigService.setInitialConfig(config);
      }),
      switchMap((config) =>
        forkJoin([
          this.bootstrapTranslations(route),
          this.venueConfigService.loadVenueConfig(config),
          this.fetchAutomaticDiscount(discountCode)
        ])
      ),
      switchMap(([, venueConfigWithProducts, automaticDiscount]) => {
        this.pendingAutomaticDiscount = automaticDiscount;

        return this.bootstrapVenueConfig(venueConfigWithProducts);
      }),
      switchMap((venueConfig) => this.getInitialAuthState(venueConfig)),
      switchMap(() => this.authService.getIsLoggedIn()),
      switchMap((isLoggedIn) => this.getGuestAccount(isLoggedIn)),
      switchMap((account: IBasicCustomer | undefined) => {
        this.setAccountDetails(account);
        this.setStorageOrder();
        this.setInitialTableNumber();
        this.setExternalBookingId();
        this.setGuestCount();
        this.setAutomaticDiscount();
        this.setShowBestPrice();

        return this.bootstrapMatomo();
      }),
      map(() => {
        this.isAppInitialized = true;
        this.setIsLoading(false);
        console.log(`App loaded in ${Date.now() - timeStart}ms`);

        return this.isAppInitialized;
      })
    );
  }

  /**
   *  Ensures Matomo Analytics and AB testing are initialized
   *  AB testing may mutate venueConfig based on test variations
   *  This should be initialized once configuration and authentication has finished. It should always return true regardless of the bootstrap success to avoid preventing bootstrap blocks.
   */
  private bootstrapMatomo(): Observable<boolean> {
    const config = this.initialConfigService.getInitialConfig();
    const venueConfig = this.venueConfigService.getVenueConfig();
    this.matomoAnalyticsService.bootstrap(venueConfig, config.checkoutSlug);

    return this.matomoAnalyticsService.getInitialized().pipe(filter((isInitialized: boolean) => isInitialized));
  }

  /** Loads available translations gracefully and configures language settings for the app */
  private bootstrapTranslations(routeSnapshot: ActivatedRouteSnapshot): Observable<IInitialConfig> {
    return this.availableTranslationsService.getAvailableTranslations().pipe(
      catchError((e: HttpErrorResponse) => {
        this.logger.warn('Error loading available translations', e?.error);
        return of(undefined);
      }),
      map((translations) => {
        this.translateService.addLangs(translations?.availableLanguageCodes || []);
        const lang = this.availableTranslationsService.getLanguageCode(routeSnapshot);
        this.setLanguageDetails(lang);

        const initConfig: IInitialConfig = { ...this.initialConfigService.getInitialConfig(), lang };
        this.initialConfigService.setInitialConfig(initConfig);
        this.navigationService.setBaseVenueCheckoutUrlPortion(initConfig);

        return initConfig;
      })
    );
  }

  private async bootstrapVenueConfig(venueConfigWithProducts: IVenueConfigWithProducts): Promise<IVenueConfig> {
    const { venueConfig, products } = venueConfigWithProducts;

    this.venueConfigService.setVenueConfig(venueConfig);
    this.productListService.setProductList(products);

    await this.dateFormatService.setVenueLocale(
      venueConfig.venueSettings.dateTimeLocale,
      this.initialConfigService.getInitialConfig().lang
    );
    this.whenAttendingService.setInitialDate(venueConfig);

    return venueConfig;
  }

  private getGuestAccount(isLoggedIn: boolean): Observable<undefined> | Observable<IBasicCustomer> {
    const config = this.venueConfigService.getVenueConfig();
    if (isLoggedIn && config.venueSettings.isGuestAccountsEnabled) {
      return this.accountService.fetchGuestAccount();
    }

    return of(undefined);
  }

  private setLanguageDetails(lang: string): void {
    this.translateService.setDefaultLang(this.availableTranslationsService.getDefaultLanguageCode());
    this.translateService.use(lang);
  }

  /** If guest accounts are not enabled, or they are not logged in, simply load any account details from storage */
  private setAccountDetails(account: IBasicCustomer | undefined): void {
    if (account) {
      this.accountService.mergeAndSetAccounts(account);
      return;
    }

    const storageAccount = this.accountService.getStorageUserAccount();
    if (storageAccount) {
      this.accountService.setUserAccount(storageAccount);
    }
  }

  private setStorageOrder() {
    const storedOrder = this.orderService.loadStorageOrder();
    if (!storedOrder) {
      this.storageService.clearTicketUserInfo();
    }
  }

  private setInitialTableNumber() {
    if (!this.venueConfigService.isFoodAndBeverageCheckout()) {
      return;
    }

    const config = this.venueConfigService.getVenueConfig();
    if (config.productsPage.isPickupOnly) {
      this.orderService.setTableNumber(CPickUpTableNumber);
      return;
    }

    const tableNumberFromUrl = this.navigationService.getTableNumberFromUrl();
    if (tableNumberFromUrl) {
      this.orderService.setTableNumber(tableNumberFromUrl);
    }
  }

  private setExternalBookingId() {
    const externalId = this.navigationService.getExternalIdFromUrl();
    if (externalId) {
      this.orderService.setExternalId(externalId);
    }
  }

  private setGuestCount() {
    const guestCount = this.guestCountService.getStoredGuestCount();
    if (guestCount) {
      this.guestCountService.setGuestCount(guestCount);
    }
  }

  private setAutomaticDiscount() {
    if (this.pendingAutomaticDiscount) {
      this.orderService.setAutomaticDiscount(this.pendingAutomaticDiscount);
    } else {
      this.orderService.removeAutomaticDiscount();
    }
  }

  private setShowBestPrice() {
    this.productBestPricingService.setVenueConfig(this.venueConfigService.getVenueConfig());
  }

  /**
   * Set the initial logged in state based on checkout setting and storage auth. Immediately attempt sign out if guest  accounts are disabled while stored auth is still valid.
   * This method is graceful, and allows bootstrap to continue regardless of result
   */
  private getInitialAuthState(venueConfig: IVenueConfig): Observable<undefined> {
    const isStoredAuthValid = this.authService.isStoredAuthValid();
    const isLoggedInValid = !!venueConfig.venueSettings.isGuestAccountsEnabled && isStoredAuthValid;

    if (isStoredAuthValid && !isLoggedInValid) {
      return this.authService.signOut();
    }

    this.authService.setIsLoggedIn(isLoggedInValid);
    return of(undefined);
  }

  private fetchAutomaticDiscount(discountCode: string | undefined): Observable<IAutomaticDiscount | undefined> {
    if (!discountCode) {
      return of(undefined);
    }

    return this.automaticDiscountService.getAutomaticDiscountByCode(discountCode).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.warn(
          `BootstrapService error: Failed to fetch automatic discount ${error.message} Status: ${error.status}`
        );
        return of(undefined);
      })
    );
  }
}
